import * as React from "react";
import { FieldWrapper } from "@montana/shared/form/field-wrapper";

import { withForm } from "../field";
import { ICheckboxProps as ICheckableProps, Checkable } from "../checkable";
import { isFieldTouched, isFieldValid } from "../field/field.component";


interface ICheckboxProps extends Omit<ICheckableProps, "type"> {
    scrollToError?: boolean | number;
}

/**
 * @author SBF & AGI
 *
 * @description
 * Can be used otuside of a <Form>, state must be handled manually.
 *
 * @example
 *  <Checkbox name="some-checkbox" isChecked={isChecked} onCheck={setIsChecked}>
 *      Label text...
 *  </Checkbox>
 */
export const Checkbox: React.FC<ICheckboxProps> = React.memo<ICheckboxProps>(({ scrollToError = false, ...props }): JSX.Element => (
    <FieldWrapper
        label={props.label || ""}
        id={props.id || ""}
        typeName="checkbox"
        errorMessage={props.errorMessage}
        isValid={props.isValid}
        isTouched={props.isTouched}
        dontRenderLabel
        compact={props.compact}
        scrollToError={scrollToError}
    >
        <Checkable type="checkbox" {...props} />
    </FieldWrapper>
));


/**
 * @author SBF
 *
 * @description
 * Must be used in the context of a <Form>
 *
 * @example
 *  <Form>
 *      <FormCheckbox name="some-checkbox" validate={requried("Must be checked")}>
 *          Label text...
 *      </FormCheckbox>
 *  </Form>
 */
export const FormCheckbox = withForm<ICheckboxProps, "isChecked" | "onCheck" | "isTouched" | "errorMessage">(
    Checkbox,
    (field, form): ICheckboxProps => ({
        isChecked: !!field.value,
        onCheck: (isChecked: boolean): void => {
            form.setFieldTouched(field.name, true);
            form.setFieldValue(field.name, isChecked);
        },
        errorMessage: (form.errors as { [key: string ]: string })[field.name],
        isValid: isFieldValid(field.name, form),
        isTouched: isFieldTouched(field.name, form),
    }),
);
